import React, { useEffect, useState } from 'react';
import { X } from "lucide-react"
import authService from '../../../Services/authService';

export default function MemberModal({ isOpen, onClose, onAdd, refreshFamilyList }) {
  const [newMember, setNewMember] = useState({})
  const [formErrors, setFormErrors] = useState({})
  const token = authService.getCurrentUsertoken();

  useEffect(() => {
    if (!isOpen) setNewMember({});
  }, [isOpen]);

  const handleInputChange = (field, value) => {
    setNewMember((prev) => ({ ...prev, [field]: value }))

    // Clear error for this field if it exists
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const updated = { ...prev }
        delete updated[field]
        return updated
      })
    }
  }

  const validateForm = () => {
    const errors = {}
    const requiredFields = ["name", "age", "relation", "gender"]

    requiredFields.forEach((field) => {
      if (!newMember[field]) {
        errors[field] = "This field is required"
      }
    })

    // Age validation
    if (newMember.age && (isNaN(Number(newMember.age)) || Number(newMember.age) <= 0 || Number(newMember.age) > 120)) {
      errors.age = "Please enter a valid age between 1-120"
    }
    
    const validName = /^[a-zA-Z\s.]+$/;
    if (newMember.name && !validName.test(newMember.name)) {
      errors.name = "Name should not contain special characters or numbers";
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleAddMember = (event) => {
    event.preventDefault();
    const currentUser = authService.getCurrentuser();
    if (!currentUser) {
      console.error("User not authenticated");
      return;
    }

    if (validateForm()) {
      fetch(authService.getApiUrl() + 'addfamily', {
        method: 'POST',
        body: JSON.stringify({
          user_id: currentUser.id,
          name: newMember.name,
          age: newMember.age,
          gender: newMember.gender,
          relation: newMember.relation,
        }),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json; charset=UTF-8',
          Authorization: `Bearer ${token}`,
        },
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error("Failed to add member");
          }
          return res.json();  // ✅ Parse response as JSON
        })
        .then((result) => {
          if (result && result.data) {
            console.log("New member added:", result.data);
            setNewMember({});
            onClose();               // ✅ Close modal
            refreshFamilyList();     // ✅ Refresh data in parent
          } else {
            console.error("Unexpected response format:", result);
          }
        })
        .catch((err) => {
          console.log("Add member failed:", err.message);
        });
      
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b border-[#E0E0E0]">
          <h2 className="text-lg font-medium text-[#4A4A4A]">Add Family Member</h2>
          <button onClick={onClose} className="text-[#4A4A4A] hover:text-[#951C25]">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <form className="needs-validation clearfix" onSubmit={handleAddMember} key="addform">
          <div className="p-4">
            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-[#4A4A4A] mb-1">
                  Name <span className="text-[#951C25]">*</span>
                </label>
                <input
                  type="text"
                  className={`w-full px-3 py-2 border ${formErrors.name ? "border-red-500" : "border-[#E0E0E0]"
                    } rounded-lg focus:outline-none focus:ring-1 focus:ring-[#951C25] focus:border-[#951C25]`}
                  value={newMember.name || ""}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  placeholder="Enter full name"
                />
                {formErrors.name && <p className="mt-1 text-xs text-red-500">{formErrors.name}</p>}
              </div>

              {/* Age */}
              <div>
                <label className="block text-sm font-medium text-[#4A4A4A] mb-1">
                  Age <span className="text-[#951C25]">*</span>
                </label>
                <input
                  type="number"
                  className={`w-full px-3 py-2 border ${formErrors.age ? "border-red-500" : "border-[#E0E0E0]"
                    } rounded-lg focus:outline-none focus:ring-1 focus:ring-[#951C25] focus:border-[#951C25]`}
                  value={newMember.age || ""}
                  onChange={(e) => handleInputChange("age", e.target.value)}
                  placeholder="Enter age"
                  min="1"
                  max="120"
                />
                {formErrors.age && <p className="mt-1 text-xs text-red-500">{formErrors.age}</p>}
              </div>

              {/* Relation */}
              <div>
                <label className="block text-sm font-medium text-[#4A4A4A] mb-1">
                  Relation <span className="text-[#951C25]">*</span>
                </label>
                <select
                  className={`w-full px-3 py-2 border ${formErrors.relation ? "border-red-500" : "border-[#E0E0E0]"
                    } rounded-lg focus:outline-none focus:ring-1 focus:ring-[#951C25] focus:border-[#951C25] bg-white`}
                  value={newMember.relation || ""}
                  onChange={(e) => handleInputChange("relation", e.target.value)}
                >
                  <option value="">Select relation</option>
                  <option value="Self">Self</option>
                  <option value="Spouse">Spouse</option>
                  <option value="Son">Son</option>
                  <option value="Daughter">Daughter</option>
                  <option value="Father">Father</option>
                  <option value="Mother">Mother</option>
                  <option value="Brother">Brother</option>
                  <option value="Sister">Sister</option>
                  <option value="Other">Other</option>
                </select>
                {formErrors.relation && <p className="mt-1 text-xs text-red-500">{formErrors.relation}</p>}
              </div>

              {/* Gender */}
              <div>
                <label className="block text-sm font-medium text-[#4A4A4A] mb-1">
                  Gender <span className="text-[#951C25]">*</span>
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="gender"
                      value="Male"
                      checked={newMember.gender === "Male"}
                      onChange={() => handleInputChange("gender", "Male")}
                      className="w-4 h-4 text-[#951C25] focus:ring-[#951C25]"
                    />
                    <span className="ml-2 text-sm text-[#4A4A4A]">Male</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="gender"
                      value="Female"
                      checked={newMember.gender === "Female"}
                      onChange={() => handleInputChange("gender", "Female")}
                      className="w-4 h-4 text-[#951C25] focus:ring-[#951C25]"
                    />
                    <span className="ml-2 text-sm text-[#4A4A4A]">Female</span>
                  </label>
                  {/* <label className="flex items-center">
                  <input
                    type="radio"
                    name="gender"
                    value="Other"
                    checked={newMember.gender === "Other"}
                    onChange={() => handleInputChange("gender", "Other")}
                    className="w-4 h-4 text-[#951C25] focus:ring-[#951C25]"
                  />
                  <span className="ml-2 text-sm text-[#4A4A4A]">Other</span>
                </label> */}
                </div>
                {formErrors.gender && <p className="mt-1 text-xs text-red-500">{formErrors.gender}</p>}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-[#E0E0E0] flex justify-end">
            <button
              type="submit"
              className="px-6 py-2 bg-[#951C25] text-white rounded-md text-sm hover:bg-[#951C25]/90"
            >
              Add
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
